import { store } from '@/store';
import {
  addAlert,
  resolveAlert,
  setConnected,
  setError,
  clearError,
  initializeAlerts,
} from '@/store/slices/alertsSlice';
import { API_ENDPOINTS } from '@/constants';
import type { AlertEvent, AlertStreamEvent } from '@/types/alerts';
import { getApiBaseUrl, getApiHeaders } from '@/store/api/headers';
import { playAlertTone } from '@/utils/alertTone';
import { notifyMissedContactAlert } from '@/utils/desktopNotification';

/**
 * AlertService manages real-time alerts streaming from backend
 *
 * Data Flow:
 * 1. Amazon Connect metrics rule triggered
 * 2. EventBridge generates event (detail-type: "Metrics Rules Matched")
 * 3. ESP backend processor reads detail.eventName (e.g., "CallVolumeSpikeEvent")
 * 4. Backend creates AlertEvent and sends via SSE stream at /alerts/real-time
 * 5. Frontend receives and dispatches to Redux store
 * 6. UI components display alerts in popups and table
 */
class AlertService {
  private abortController: AbortController | null = null;
  private isListeningFlag = false;

  /**
   * Parses a single line from SSE stream (data: {...}\n)
   */
  private parseSSELine(line: string): AlertStreamEvent | null {
    if (!line.startsWith('data: ')) {
      return null;
    }

    try {
      const jsonStr = line.slice(6); // Remove "data: " prefix
      return JSON.parse(jsonStr) as AlertStreamEvent;
    } catch (err) {
      console.error('Failed to parse SSE line:', err, line);
      return null;
    }
  }

  /**
   * Opens SSE connection and listens for alert events
   */
  private async fetchAlertsStream() {
    const token = localStorage.getItem('token');

    if (!token) {
      const error = 'Authentication token not found';
      console.error(error);
      store.dispatch(setError(error));
      return;
    }

    try {
      this.abortController = new AbortController();
      const headers = await getApiHeaders({ eventStream: true });
      const response = await fetch(
        `${getApiBaseUrl()}${API_ENDPOINTS.ALERTS.REAL_TIME}`,
        {
          method: 'GET',
          headers,
          signal: this.abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      store.dispatch(setConnected(true));
      store.dispatch(clearError());

      // Read stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let streamEnded = false;

      while (!streamEnded) {
        const { done, value } = await reader.read();

        if (done) {
          streamEnded = true;
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Split by double newlines (SSE ends events with \n\n)
        const blocks = buffer.split('\n\n');

        // Keep the last incomplete block in buffer
        buffer = blocks.pop() || '';

        for (const block of blocks) {
          if (!block.trim()) continue;
          const dataLine = block
            .split('\n')
            .find(line => line.startsWith('data: '));
          if (!dataLine) continue;
          const event = this.parseSSELine(dataLine);
          if (event) {
            this.handleStreamEvent(event);
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('SSE connection aborted');
        return;
      }

      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      console.error('SSE connection error:', errorMsg);
      store.dispatch(setError(errorMsg));
      store.dispatch(setConnected(false));

      // Attempt reconnect after delay
      setTimeout(() => {
        if (this.isListeningFlag) {
          console.log('Attempting to reconnect to alerts stream...');
          this.fetchAlertsStream();
        }
      }, 5000);
    }
  }

  /**
   * Handles incoming SSE events
   */
  private handleStreamEvent(event: AlertStreamEvent) {
    switch (event.type) {
      case 'init':
        store.dispatch(initializeAlerts(event.alerts));
        break;

      case 'alert':
        store.dispatch(addAlert(event.alert));
        this.maybeNotifyMissedContact(event.alert);
        break;

      case 'alert_resolved':
        store.dispatch(resolveAlert(event.alertId));
        break;

      case 'ping':
        // Keep-alive messages are intentionally ignored by consumers.
        break;

      default:
        console.warn('Unknown event type:', event);
    }
  }

  /**
   * Fire audible + desktop notification for missed call/chat alerts.
   * Other alert types stay silent (popup/table only).
   */
  private maybeNotifyMissedContact(alert: AlertEvent): void {
    if (alert.type !== 'MISSED_CALL' && alert.type !== 'MISSED_CHAT') return;
    playAlertTone();
    notifyMissedContactAlert(alert);
  }

  /**
   * Start listening to alerts stream
   */
  startListening() {
    if (this.isListeningFlag) {
      console.warn('Already listening to alerts');
      return;
    }

    this.isListeningFlag = true;
    this.fetchAlertsStream();
  }

  /**
   * Stop listening to alerts stream
   */
  stopListening() {
    this.isListeningFlag = false;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    store.dispatch(setConnected(false));
  }

  /**
   * Check if currently listening
   */
  isListening() {
    return this.isListeningFlag;
  }

  /**
   * Resolve an alert on the server (manager). Backend broadcasts `alert_resolved` on the alerts SSE stream.
   */
  async requestResolveAlert(alertId: string): Promise<void> {
    const headers = await getApiHeaders();
    const response = await fetch(
      `${getApiBaseUrl()}${API_ENDPOINTS.ALERTS.MANAGER_RESOLVE}/${encodeURIComponent(alertId)}`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }
}

export const alertService = new AlertService();
